import { useCallback, useMemo } from 'react';
import constate from 'constate';
import {
  BootFlags,
  getBootFlag,
  logError,
  chronosphereLogsClient,
  Analytics,
  AnalyticsInternal,
} from '@chronosphereio/core';
import { LocalDevAnalytics } from './local-dev-analytics';
import { NoOpAnalytics } from './no-op-analytics';
import { FullStoryAnalytics } from './fullstory-analytics';
import { useAddUserContextToAnalytics } from './add-user-context';
import { trackUiAnalytics } from '@/utils/monitoring';
import { evalClient } from '@/clients/eval-client';

function useAnalyticsStore(): Analytics {
  // Init the analytics client when the provider mounts
  const client = getAnalyticsClient();

  // Once the user is logged in, we can add some additional information about them
  useAddUserContextToAnalytics(client);

  // Wrap client methods (track, trackMetric, trackEval)
  const track: Analytics['track'] = useCallback(
    (...args) => {
      trackUiAnalytics({
        // For the base method, only track the eventName to avoid unexpected cardinality
        event_name: args[0],
      });
      chronosphereLogsClient.track(...args);
      return client.track(...args);
    },
    [client]
  );

  const trackMetric: Analytics['trackMetric'] = useCallback(
    (...args) => {
      trackUiAnalytics({
        event_name: args[0],
        // For trackMetric, pass through any additional labels the caller has provided
        ...args[1],
      });
      chronosphereLogsClient.track(...args);
      return client.track(...args);
    },
    [client]
  );

  const _trackEval: NonNullable<Analytics['trackEval']> = useCallback((args) => trackEval(args), []);

  const _trackEvalAsync: NonNullable<Analytics['trackEvalAsync']> = useCallback((args) => trackEvalAsync(args), []);

  // Memoize in case entire object is used in an effect/callback
  return useMemo(
    () => ({
      track,
      trackMetric,
      trackEval: _trackEval,
      trackEvalAsync: _trackEvalAsync,
      getSessionUrl: client.getSessionUrl,
    }),
    [track, trackMetric, _trackEval, _trackEvalAsync, client]
  );
}

export const [AnalyticsProvider, useAnalytics] = constate(useAnalyticsStore);

/**
 * Track evaluation of an AI feature.
 * Use when you need to handle the result.
 * Prefer useAnalytics().trackEvalAsync inside React components.
 */
export const trackEvalAsync: NonNullable<Analytics['trackEvalAsync']> = async (args) => {
  chronosphereLogsClient.track('eval', args);
  getAnalyticsClient().track('eval', args);
  await evalClient.recordEval({
    feature: args.feature,
    version: args.version?.toString(),
    messages: args.input ? [{ role: 'user', content: args.input }] : undefined,
    output: args.output,
    evals: args.eval ? [args.eval] : args.evals,
    metadata: args.metadata,
  });
};

/**
 * Track evaluation of an AI feature.
 * Use when you want to fire-and-forget.
 * Prefer useAnalytics().trackEval inside React components.
 */
export const trackEval: NonNullable<Analytics['trackEval']> = (args) => {
  trackEvalAsync(args).catch((err) => {
    console.error('Failed to track eval:', err);
  });
};

/**
 * Singleton instance of the analytics client.
 */
let client: AnalyticsInternal | undefined = undefined;

function getAnalyticsClient(): AnalyticsInternal {
  if (client !== undefined) return client;

  try {
    const fullStoryMode = getBootFlag(BootFlags.FULL_STORY_MODE);

    let newClient: AnalyticsInternal;
    if (process.env.NODE_ENV === 'development') {
      newClient = new LocalDevAnalytics();
    } else if (fullStoryMode === 'enabled' || fullStoryMode === 'relayed') {
      const shouldUseRelay = fullStoryMode === 'relayed';
      newClient = new FullStoryAnalytics(shouldUseRelay);
    } else {
      newClient = new NoOpAnalytics();
    }

    newClient.initialize?.();

    // Cache the singleton instance
    client = newClient;
  } catch (err) {
    // Use the no-op client if something is wrong
    logError(err);
    client = new NoOpAnalytics();
  }

  return client;
}
